import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import API from '../api/axios';
import './UniversityPoolManagement.css';

const UniversityPoolManagement = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  
  const [activeTab, setActiveTab] = useState('pools');
  const [poolPapers, setPoolPapers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [extractingTemplate, setExtractingTemplate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolQuestions, setPoolQuestions] = useState([]);
  const [newPool, setNewPool] = useState({
    subject_id: '',
    paper_title: '',
    paper_description: '',
    total_marks: 100,
    template_id: null,
    difficulty: 'medium',
    language: 'english',
    min_variations_per_question: 40,
    max_variations_per_question: 100,
    min_sets_to_generate: 5,
    max_sets_to_generate: 10,
    question_types: {
      mcq: true,
      short_answer: true,
      long_answer: false
    },
    marks_distribution: {
      mcq: 1,
      short_answer: 2,
      long_answer: 5
    }
  });
  const [newQuestion, setNewQuestion] = useState({
    question_number: '',
    question_type: 'short_answer',
    marks: 5,
    difficulty: 'medium',
    section_name: '',
    has_sub_questions: false
  });
  const [newSubQuestion, setNewSubQuestion] = useState({
    sub_question_number: '',
    question_type: 'short_answer',
    marks: 5,
    difficulty: 'medium',
    section_name: ''
  });

  const fetchPoolPapers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/university-pool/pool-papers');
      
      if (data.success) {
        setPoolPapers(data.pool_papers || []);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch pool papers', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchPoolPapers();
    fetchSubjects();
    fetchTemplates();
  }, [fetchPoolPapers]);

  const fetchSubjects = async () => {
    try {
      const { data } = await API.get('/subjects');
      if (data.success) {
        setSubjects(data.subjects || []);
      }
    } catch (err) {
      console.error('Failed to fetch subjects:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await API.get('/templates?context=pool');
      if (data.success) {
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const handleTemplateSelect = async (templateId) => {
    if (!templateId) {
      return;
    }

    try {
      const { data } = await API.get(`/templates/${templateId}`);
      let questions = data.template?.questions || data.questions;

      if (typeof questions === 'string') {
        questions = JSON.parse(questions);
      }

      if (questions && questions.length > 0) {
        // Auto-populate question types and marks distribution from template
        const questionTypes = {
          mcq: false,
          short_answer: false,
          long_answer: false
        };

        const marksDistribution = {
          mcq: 1,
          short_answer: 2,
          long_answer: 5
        };

        // Analyze template questions to determine types and marks
        questions.forEach(q => {
          const type = q.question_type || q.type;
          const marks = q.marks || 2;

          if (type === 'mcq' || type === 'multiple_choice') {
            questionTypes.mcq = true;
            marksDistribution.mcq = marks;
          } else if (type === 'short_answer' || type === 'short') {
            questionTypes.short_answer = true;
            marksDistribution.short_answer = marks;
          } else if (type === 'long_answer' || type === 'long' || type === 'essay') {
            questionTypes.long_answer = true;
            marksDistribution.long_answer = marks;
          }
        });

        // Update the form with template data
        setNewPool(prev => ({
          ...prev,
          template_id: templateId,
          total_marks: data.template?.total_marks || prev.total_marks,
          question_types: questionTypes,
          marks_distribution: marksDistribution
        }));

        showToast('Template loaded! Question types and marks auto-populated.', 'success');
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      showToast('Failed to load template details', 'error');
    }
  };

  const handleExtractTemplateFromPDF = async () => {
    if (!pdfFile) {
      showToast('Please select a PDF file first', 'error');
      return;
    }

    if (!newPool.paper_title) {
      showToast('Please enter a paper title first', 'error');
      return;
    }

    try {
      setExtractingTemplate(true);
      
      // Upload PDF
      const formData = new FormData();
      formData.append('pdf', pdfFile);
      formData.append('template_name', `[University Pool] ${newPool.paper_title}`);
      formData.append('template_description', newPool.paper_description || 'Extracted from university pool PDF');

      const { data } = await API.post('/template-extraction/extract-structure', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (data.success) {
        showToast('Template extracted successfully!', 'success');
        
        // Refresh templates and auto-select the new one
        await fetchTemplates();
        
        // Find and select the newly created template
        const newTemplate = data.template;
        if (newTemplate && newTemplate.template_id) {
          setNewPool(prev => ({ 
            ...prev, 
            template_id: newTemplate.template_id,
            total_marks: newTemplate.total_marks || prev.total_marks
          }));
        }
        
        // Clear the PDF file
        setPdfFile(null);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to extract template from PDF', 'error');
    } finally {
      setExtractingTemplate(false);
    }
  };

  const fetchPoolQuestions = async (poolId) => {
    try {
      const { data } = await API.get(`/university-pool/pool-papers/${poolId}/questions`);
      
      if (data.success) {
        setPoolQuestions(data.questions || []);
        setSelectedPool(poolId);
        setActiveTab('questions');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch pool questions', 'error');
    }
  };

  const handleCreatePool = async (e) => {
    e.preventDefault();
    
    if (!newPool.subject_id || !newPool.paper_title) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      const { data } = await API.post('/university-pool/pool-papers/create', {
        ...newPool,
        university_id: user.university_id
      });

      if (data.success) {
        showToast('University pool paper created successfully', 'success');
        setShowCreateModal(false);
        setNewPool({
          subject_id: '',
          paper_title: '',
          paper_description: '',
          total_marks: 100,
          template_id: null,
          difficulty: 'medium',
          language: 'english',
          min_variations_per_question: 40,
          max_variations_per_question: 100,
          min_sets_to_generate: 5,
          max_sets_to_generate: 10,
          question_types: {
            mcq: true,
            short_answer: true,
            long_answer: false
          },
          marks_distribution: {
            mcq: 1,
            short_answer: 2,
            long_answer: 5
          }
        });
        setPdfFile(null);
        fetchPoolPapers();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create pool paper', 'error');
    }
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    
    if (!newQuestion.question_number) {
      showToast('Please enter a question number', 'error');
      return;
    }

    try {
      const { data } = await API.post(`/university-pool/pool-papers/${selectedPool}/questions/create`, newQuestion);

      if (data.success) {
        showToast(`Question ${newQuestion.question_number} created successfully`, 'success');
        setShowQuestionModal(false);
        setNewQuestion({
          question_number: '',
          question_type: 'short_answer',
          marks: 5,
          difficulty: 'medium',
          section_name: '',
          has_sub_questions: false
        });
        fetchPoolQuestions(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create question', 'error');
    }
  };

  const handleCreateSubQuestion = async (questionId) => {
    if (!newSubQuestion.sub_question_number) {
      showToast('Please enter a sub-question number', 'error');
      return;
    }

    try {
      const { data } = await API.post(
        `/university-pool/pool-papers/${selectedPool}/questions/${questionId}/sub-questions/create`,
        newSubQuestion
      );

      if (data.success) {
        showToast(`Sub-question created successfully`, 'success');
        setNewSubQuestion({
          sub_question_number: '',
          question_type: 'short_answer',
          marks: 5,
          difficulty: 'medium',
          section_name: ''
        });
        fetchPoolQuestions(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create sub-question', 'error');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { class: 'draft', text: 'Draft' },
      'active': { class: 'active', text: 'Active' },
      'locked': { class: 'locked', text: 'Locked' },
      'completed': { class: 'completed', text: 'Completed' }
    };
    
    const statusInfo = statusMap[status] || { class: 'draft', text: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="university-pool-management-container">
      <div className="page-header">
        <h1>University Pool Management</h1>
        <p>Create and manage university-wide question pools</p>
        <div className="role-badge">
          <span className="badge university-admin">University Admin</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button 
            className={`tab-button ${activeTab === 'pools' ? 'active' : ''}`}
            onClick={() => setActiveTab('pools')}
          >
            Pool Papers ({poolPapers.length})
          </button>
          {selectedPool && (
            <button 
              className={`tab-button ${activeTab === 'questions' ? 'active' : ''}`}
              onClick={() => setActiveTab('questions')}
            >
              Questions Structure
            </button>
          )}
        </div>

        <div className="tab-content">
          {activeTab === 'pools' && (
            <div className="pools-section">
              <div className="section-header">
                <h2>University Pool Papers</h2>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  + Create New Pool Paper
                </button>
              </div>

              {poolPapers.length === 0 ? (
                <div className="empty-state">
                  <p>No pool papers created yet</p>
                </div>
              ) : (
                <div className="pools-grid">
                  {poolPapers.map(pool => (
                    <div key={pool.pool_paper_id} className="pool-card">
                      <div className="pool-header">
                        <h3>{pool.subject_name} - {pool.paper_title}</h3>
                        {getStatusBadge(pool.status)}
                      </div>
                      
                      <div className="pool-details">
                        <p><strong>Description:</strong> {pool.paper_description || 'No description'}</p>
                        <p><strong>Total Marks:</strong> {pool.total_marks}</p>
                        <p><strong>Questions:</strong> {pool.question_count}</p>
                        <p><strong>Variations:</strong> {pool.total_variations}</p>
                        <p><strong>Examiners:</strong> {pool.examiner_count}</p>
                        <p><strong>Created:</strong> {new Date(pool.created_at).toLocaleDateString()}</p>
                        <p><strong>Created by:</strong> {pool.created_by_name}</p>
                      </div>

                      <div className="pool-actions">
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => fetchPoolQuestions(pool.pool_paper_id)}
                        >
                          Manage Questions
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'questions' && selectedPool && (
            <div className="questions-section">
              <div className="section-header">
                <h2>Questions Structure</h2>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowQuestionModal(true)}
                >
                  + Add Question
                </button>
              </div>

              {poolQuestions.length === 0 ? (
                <div className="empty-state">
                  <p>No questions added yet</p>
                </div>
              ) : (
                <div className="questions-list">
                  {poolQuestions.map(question => (
                    <div key={question.pool_question_id} className="question-card">
                      <div className="question-header">
                        <h3>Question {question.question_number}</h3>
                        <div className="question-meta">
                          <span className="question-type">{question.question_type}</span>
                          <span className="question-marks">{question.marks} marks</span>
                          <span className="question-difficulty">{question.difficulty}</span>
                        </div>
                      </div>
                      
                      <div className="question-details">
                        {question.section_name && (
                          <p><strong>Section:</strong> {question.section_name}</p>
                        )}
                        <p><strong>Sub-questions:</strong> {question.sub_question_count}</p>
                        <p><strong>Variations:</strong> {question.variation_count}</p>
                      </div>

                      {/* Sub-questions */}
                      {question.sub_questions && question.sub_questions.length > 0 && (
                        <div className="sub-questions">
                          <h4>Sub-questions:</h4>
                          <div className="sub-questions-grid">
                            {question.sub_questions.map(subQ => (
                              <div key={subQ.pool_sub_question_id} className="sub-question-item">
                                <div className="sub-question-header">
                                  <span className="sub-question-number">{subQ.full_question_number}</span>
                                  <span className="sub-question-marks">{subQ.marks} marks</span>
                                </div>
                                <div className="sub-question-stats">
                                  <span>Variations: {subQ.variation_count}</span>
                                  <span>Pool: {subQ.pool_variation_count}</span>
                                  <span>Selected: {subQ.selected_variation_count}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add Sub-question Form */}
                      <div className="add-sub-question">
                        <h4>Add Sub-question:</h4>
                        <div className="sub-question-form">
                          <input
                            type="text"
                            placeholder="Sub-question number (e.g., a, b, i, ii)"
                            value={newSubQuestion.sub_question_number}
                            onChange={(e) => setNewSubQuestion({...newSubQuestion, sub_question_number: e.target.value})}
                            className="form-input"
                          />
                          <select
                            value={newSubQuestion.question_type}
                            onChange={(e) => setNewSubQuestion({...newSubQuestion, question_type: e.target.value})}
                            className="form-select"
                          >
                            <option value="short_answer">Short Answer</option>
                            <option value="long_answer">Long Answer</option>
                            <option value="mcq">Multiple Choice</option>
                            <option value="numerical">Numerical</option>
                          </select>
                          <input
                            type="number"
                            placeholder="Marks"
                            value={newSubQuestion.marks}
                            onChange={(e) => setNewSubQuestion({...newSubQuestion, marks: parseInt(e.target.value)})}
                            className="form-input"
                            min="1"
                          />
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleCreateSubQuestion(question.pool_question_id)}
                          >
                            Add Sub-question
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Pool Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New University Pool Paper</h3>
              <button 
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreatePool}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <select
                    className="form-select"
                    value={newPool.subject_id}
                    onChange={(e) => setNewPool({...newPool, subject_id: e.target.value})}
                    required
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.subject_id} value={subject.subject_id}>
                        {subject.subject_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Paper Title *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newPool.paper_title}
                    onChange={(e) => setNewPool({...newPool, paper_title: e.target.value})}
                    placeholder="e.g., Physics - Mechanics, Mathematics - Calculus"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    value={newPool.paper_description}
                    onChange={(e) => setNewPool({...newPool, paper_description: e.target.value})}
                    placeholder="Brief description of the paper content"
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Total Marks</label>
                    <input
                      type="number"
                      className="form-input"
                      value={newPool.total_marks}
                      onChange={(e) => setNewPool({...newPool, total_marks: parseInt(e.target.value)})}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Difficulty Level</label>
                    <select
                      className="form-select"
                      value={newPool.difficulty}
                      onChange={(e) => setNewPool({...newPool, difficulty: e.target.value})}
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Language</label>
                    <select
                      className="form-select"
                      value={newPool.language}
                      onChange={(e) => setNewPool({...newPool, language: e.target.value})}
                    >
                      <option value="english">English</option>
                      <option value="hindi">Hindi</option>
                    </select>
                  </div>
                </div>

                {/* Template Section */}
                <div className="form-section">
                  <h4>Template Configuration</h4>
                  
                  <div className="form-group">
                    <label className="form-label">Extract Template from PDF</label>
                    <div className="pdf-upload-section">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setPdfFile(e.target.files[0])}
                        className="form-input"
                      />
                      {pdfFile && (
                        <div className="pdf-actions" style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span className="pdf-name">📄 {pdfFile.name}</span>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleExtractTemplateFromPDF}
                            disabled={extractingTemplate}
                          >
                            {extractingTemplate ? 'Extracting...' : 'Extract Template'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Or Select Existing Template</label>
                    <select
                      className="form-select"
                      value={newPool.template_id || ''}
                      onChange={(e) => {
                        const templateId = e.target.value || null;
                        if (templateId) {
                          handleTemplateSelect(templateId);
                        } else {
                          setNewPool(prev => ({ ...prev, template_id: null }));
                        }
                      }}
                    >
                      <option value="">No Template - Create from scratch</option>
                      {templates.map(template => (
                        <option key={template.template_id} value={template.template_id}>
                          {template.template_name} ({template.question_count} questions, {template.total_marks} marks)
                        </option>
                      ))}
                    </select>
                    <small className="form-help">
                      Select a template to auto-populate question types and marks, or extract one from PDF above.
                    </small>
                  </div>
                </div>

                {/* Pool Configuration */}
                <div className="form-section">
                  <h4>Pool Configuration</h4>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Min Variations per Question</label>
                      <input
                        type="number"
                        className="form-input"
                        value={newPool.min_variations_per_question}
                        onChange={(e) => setNewPool({...newPool, min_variations_per_question: parseInt(e.target.value)})}
                        min="4"
                        max="50"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Max Variations per Question</label>
                      <input
                        type="number"
                        className="form-input"
                        value={newPool.max_variations_per_question}
                        onChange={(e) => setNewPool({...newPool, max_variations_per_question: parseInt(e.target.value)})}
                        min="10"
                        max="200"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Min Sets to Generate</label>
                      <input
                        type="number"
                        className="form-input"
                        value={newPool.min_sets_to_generate}
                        onChange={(e) => setNewPool({...newPool, min_sets_to_generate: parseInt(e.target.value)})}
                        min="3"
                        max="10"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Max Sets to Generate</label>
                      <input
                        type="number"
                        className="form-input"
                        value={newPool.max_sets_to_generate}
                        onChange={(e) => setNewPool({...newPool, max_sets_to_generate: parseInt(e.target.value)})}
                        min="5"
                        max="20"
                      />
                    </div>
                  </div>
                </div>

                {/* Question Types */}
                <div className="form-section">
                  <h4>Question Types</h4>
                  
                  <div className="question-types-grid">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newPool.question_types.mcq}
                        onChange={(e) => setNewPool({
                          ...newPool,
                          question_types: { ...newPool.question_types, mcq: e.target.checked }
                        })}
                      />
                      Multiple Choice Questions (MCQ)
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newPool.question_types.short_answer}
                        onChange={(e) => setNewPool({
                          ...newPool,
                          question_types: { ...newPool.question_types, short_answer: e.target.checked }
                        })}
                      />
                      Short Answer Questions
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newPool.question_types.long_answer}
                        onChange={(e) => setNewPool({
                          ...newPool,
                          question_types: { ...newPool.question_types, long_answer: e.target.checked }
                        })}
                      />
                      Long Answer Questions
                    </label>
                  </div>

                  <div className="marks-distribution">
                    <h5>Marks Distribution</h5>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">MCQ Marks</label>
                        <input
                          type="number"
                          className="form-input"
                          value={newPool.marks_distribution.mcq}
                          onChange={(e) => setNewPool({
                            ...newPool,
                            marks_distribution: { ...newPool.marks_distribution, mcq: parseInt(e.target.value) }
                          })}
                          min="1"
                          max="10"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Short Answer Marks</label>
                        <input
                          type="number"
                          className="form-input"
                          value={newPool.marks_distribution.short_answer}
                          onChange={(e) => setNewPool({
                            ...newPool,
                            marks_distribution: { ...newPool.marks_distribution, short_answer: parseInt(e.target.value) }
                          })}
                          min="1"
                          max="10"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Long Answer Marks</label>
                        <input
                          type="number"
                          className="form-input"
                          value={newPool.marks_distribution.long_answer}
                          onChange={(e) => setNewPool({
                            ...newPool,
                            marks_distribution: { ...newPool.marks_distribution, long_answer: parseInt(e.target.value) }
                          })}
                          min="1"
                          max="20"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  Create Pool Paper
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Question Modal */}
      {showQuestionModal && (
        <div className="modal-overlay" onClick={() => setShowQuestionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Question</h3>
              <button 
                className="modal-close"
                onClick={() => setShowQuestionModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateQuestion}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Question Number *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newQuestion.question_number}
                    onChange={(e) => setNewQuestion({...newQuestion, question_number: e.target.value})}
                    placeholder="e.g., Q1, Q2, 1, 2"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Question Type</label>
                  <select
                    className="form-select"
                    value={newQuestion.question_type}
                    onChange={(e) => setNewQuestion({...newQuestion, question_type: e.target.value})}
                  >
                    <option value="short_answer">Short Answer</option>
                    <option value="long_answer">Long Answer</option>
                    <option value="mcq">Multiple Choice</option>
                    <option value="numerical">Numerical</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Marks</label>
                  <input
                    type="number"
                    className="form-input"
                    value={newQuestion.marks}
                    onChange={(e) => setNewQuestion({...newQuestion, marks: parseInt(e.target.value)})}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Difficulty</label>
                  <select
                    className="form-select"
                    value={newQuestion.difficulty}
                    onChange={(e) => setNewQuestion({...newQuestion, difficulty: e.target.value})}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Section Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newQuestion.section_name}
                    onChange={(e) => setNewQuestion({...newQuestion, section_name: e.target.value})}
                    placeholder="e.g., Section A, Part I"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newQuestion.has_sub_questions}
                      onChange={(e) => setNewQuestion({...newQuestion, has_sub_questions: e.target.checked})}
                    />
                    Has Sub-questions
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowQuestionModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  Add Question
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversityPoolManagement;